import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { isOptedOut, optOut } from "@/lib/analytics";

// The informational analytics notice (ADR-0050, #310): a low-friction bar, NOT a
// blocking consent gate. Analytics is on by default (consent-free cookieless,
// legitimate interest); this simply discloses it, links to the Polityka
// prywatności (#305), and offers a one-tap opt-out. Quiet chrome per DESIGN.md —
// a flat, hairline-topped bottom bar; the single coral thread is the policy link.
//
// It self-suppresses once the visitor acknowledges (a functional UI preference)
// OR has opted out (the seam's functional flag) — both permitted without consent.
const DISMISS_STORAGE_KEY = "pufimi.analytics.notice_dismissed";

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY) === "1";
  } catch {
    // Storage unavailable (e.g. Safari private mode) — treat as not dismissed.
    return false;
  }
}

function persistDismissed(): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, "1");
  } catch {
    // Storage unavailable — the bar simply reappears on the next boot. Harmless.
  }
}

export function AnalyticsNotice() {
  const { t } = useTranslation("analytics");
  // Visibility is derived from localStorage (the opt-out + acknowledged flags),
  // which does NOT exist during the build-time prerender (ADR-0053). Reading it
  // in the INITIAL render would make the server always render the bar while the
  // client renders per storage — a hydration mismatch. Under this app's
  // full-document `hydrateRoot` (client.tsx) that mismatch is fatal to the bar's
  // interactivity: React can't cleanly reconcile it, so the bar's buttons never
  // get their click handlers and the bar can't be dismissed. So the first
  // render is UNCONDITIONALLY hidden — byte-identical to the server — and the
  // real decision is deferred to an effect that runs only AFTER hydration. Same
  // post-hydration-read discipline as `applyStoredLanguage` (see i18n.ts).
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isOptedOut() && !isDismissed()) setVisible(true);
  }, []);

  if (!visible) return null;

  const acknowledge = () => {
    persistDismissed();
    setVisible(false);
  };

  const declineAnalytics = () => {
    optOut(); // persist the opt-out flag + stop capturing for this session
    persistDismissed(); // opting out also dismisses the bar
    setVisible(false);
  };

  return (
    // A landmark region (named <section>), never a dialog: it must not trap focus
    // or gate the page — analytics is on by default and this only discloses it.
    <section
      aria-label={t("notice.label")}
      data-testid="analytics-notice"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
    >
      <div className="mx-auto flex w-full max-w-screen-md flex-col gap-3 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="text-pretty text-muted-foreground">
          {t("notice.message")}{" "}
          <Link
            to="/polityka-prywatnosci"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {t("notice.policyLink")}
          </Link>
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            className="rounded-full"
            data-testid="analytics-notice-optout"
            onClick={declineAnalytics}
          >
            {t("notice.optOut")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            data-testid="analytics-notice-dismiss"
            onClick={acknowledge}
          >
            {t("notice.dismiss")}
          </Button>
        </div>
      </div>
    </section>
  );
}
